import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import toast from 'react-hot-toast';
import {
  PackageCheck, RefreshCw, X, Clock, CheckCircle2, Bell,
} from 'lucide-react';

const STATUS_LABEL = {
  pendente: 'Pendente',
  separando: 'Separando',
  pronto: 'Pronto / Separado',
  entregue: 'Entregue',
  cancelado: 'Cancelado',
};

const STATUS_CLASS = {
  pendente: 'bg-amber-100 text-amber-800',
  separando: 'bg-sky-100 text-sky-800',
  pronto: 'bg-emerald-100 text-emerald-800',
  entregue: 'bg-slate-100 text-slate-700',
  cancelado: 'bg-red-100 text-red-700',
};

const fmt = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('pt-BR');
  } catch {
    return String(v);
  }
};

const numeroSol = (r) =>
  r.numero || r.label || (r.id ? `#${String(r.id).slice(0, 8).toUpperCase()}` : '—');

const isAberta = (r) => ['pendente', 'separando', 'pronto'].includes(r.status);

const nowLocalParts = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return {
    data: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    hora: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

const ReposicoesEstoque = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('id') || '';

  const [tecnicos, setTecnicos] = useState([]);
  const [tecnicoId, setTecnicoId] = useState('');
  const [filtro, setFiltro] = useState('abertas');
  const [reposicoes, setReposicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acaoId, setAcaoId] = useState(null);

  const [modalEntrega, setModalEntrega] = useState(null); // solicitacao
  const [formEntrega, setFormEntrega] = useState(nowLocalParts());
  const [salvandoEntrega, setSalvandoEntrega] = useState(false);

  const rowRefs = useRef({});
  const autoOpenedRef = useRef(false);

  const loadTecnicos = useCallback(async () => {
    try {
      const res = await api.get('/users', { params: { funcao: 'tecnico' } });
      setTecnicos(res.data?.users || []);
    } catch {
      toast.error('Erro ao carregar técnicos');
    }
  }, []);

  const loadReposicoes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (tecnicoId) params.tecnico_id = tecnicoId;
      if (filtro === 'abertas') params.abertas = true;
      else if (filtro === 'entregue') params.status = 'entregue';

      const res = await api.get('/estoque/reposicoes', { params });
      setReposicoes(res.data?.reposicoes || []);
    } catch {
      toast.error('Erro ao carregar requisições');
      setReposicoes([]);
    } finally {
      setLoading(false);
    }
  }, [tecnicoId, filtro]);

  useEffect(() => {
    loadTecnicos();
  }, [loadTecnicos]);

  useEffect(() => {
    loadReposicoes();
  }, [loadReposicoes]);

  // Destaca e sugere modal de entrega quando ?id= vem do e-mail
  useEffect(() => {
    if (!highlightId || loading || reposicoes.length === 0) return;
    const sol = reposicoes.find((r) => r.id === highlightId);
    if (!sol) return;

    const el = rowRefs.current[highlightId];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (!autoOpenedRef.current && isAberta(sol) && !sol.entrega_base_data_hora) {
      autoOpenedRef.current = true;
      setFormEntrega(nowLocalParts());
      setModalEntrega(sol);
    }
  }, [highlightId, loading, reposicoes]);

  const marcarSeparado = async (sol) => {
    setAcaoId(sol.id);
    try {
      await api.post(`/estoque/reposicao/${sol.id}/marcar-separado`);
      toast.success(`Técnico avisado: material separado (${numeroSol(sol)})`);
      await loadReposicoes();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao marcar como separado';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao marcar como separado');
    } finally {
      setAcaoId(null);
    }
  };

  const abrirModalEntrega = (sol) => {
    setFormEntrega(nowLocalParts());
    setModalEntrega(sol);
  };

  const confirmarEntregaBase = async (e) => {
    e.preventDefault();
    if (!modalEntrega) return;
    const { data, hora } = formEntrega;
    if (!data || !hora) {
      toast.error('Informe dia e hora');
      return;
    }
    const dataHora = new Date(`${data}T${hora}:00`);
    if (Number.isNaN(dataHora.getTime())) {
      toast.error('Data/hora inválida');
      return;
    }

    setSalvandoEntrega(true);
    try {
      await api.post(`/estoque/reposicao/${modalEntrega.id}/confirmar-entrega-base`, {
        data_hora: dataHora.toISOString(),
      });
      toast.success(`Entrega registrada: ${numeroSol(modalEntrega)}`);
      setModalEntrega(null);
      if (highlightId === modalEntrega.id) {
        setSearchParams({}, { replace: true });
      }
      await loadReposicoes();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao confirmar entrega';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao confirmar entrega');
    } finally {
      setSalvandoEntrega(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <PackageCheck className="w-7 h-7 text-emerald-600" />
            Requisições de reposição
          </h1>
          <p className="text-slate-600 mt-1">
            Acompanhe status, avise quando o material estiver separado e confirme a entrega ao técnico.
          </p>
        </div>
        <Link
          to="/estoque"
          className="text-sm text-emerald-700 hover:underline self-start sm:self-auto"
        >
          Ir para Estoque
        </Link>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-start md:items-end bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <div className="flex-1 w-full md:max-w-xs">
          <label className="block text-sm font-medium text-slate-600 mb-1">Técnico</label>
          <select
            value={tecnicoId}
            onChange={(e) => setTecnicoId(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-4 py-3 bg-white"
          >
            <option value="">Todos</option>
            {tecnicos.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Filtro</label>
          <select
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="border border-slate-200 rounded-xl px-4 py-3 bg-white"
          >
            <option value="abertas">Em aberto</option>
            <option value="entregue">Recebidas pelo técnico</option>
            <option value="todas">Todas</option>
          </select>
        </div>
        <button
          type="button"
          onClick={loadReposicoes}
          className="px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 flex items-center gap-2 hover:bg-slate-50"
        >
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-100">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Nº</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Técnico</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Itens</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Solicitado</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Separado</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Entrega (base)</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Recebido</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {reposicoes.map((r) => {
                  const aberta = isAberta(r);
                  const jaSeparado = Boolean(r.separado_em) || r.status === 'pronto' || r.status === 'entregue';
                  const jaEntregaBase = Boolean(r.entrega_base_data_hora);
                  const highlighted = highlightId && r.id === highlightId;
                  return (
                    <tr
                      key={r.id}
                      ref={(el) => { if (el) rowRefs.current[r.id] = el; }}
                      className={`border-b border-slate-100 ${highlighted ? 'bg-emerald-50' : ''}`}
                    >
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLASS[r.status] || 'bg-slate-100 text-slate-700'}`}>
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm font-mono font-semibold">{numeroSol(r)}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{r.tecnico_nome || '—'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600 max-w-xs">
                        {(r.itens || []).map((i) => `${i.codigo || i.nome}×${i.quantidade_solicitada}`).join(', ') || '—'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">{fmt(r.data_solicitacao)}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">{fmt(r.separado_em)}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">{fmt(r.entrega_base_data_hora)}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">{fmt(r.data_entrega || r.confirmado_em)}</td>
                      <td className="py-3 px-4">
                        {aberta ? (
                          <div className="flex flex-col gap-2 min-w-[160px]">
                            <button
                              type="button"
                              disabled={jaSeparado || acaoId === r.id}
                              onClick={() => marcarSeparado(r)}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Bell className="w-3.5 h-3.5" />
                              {jaSeparado ? 'Já separado' : 'Avisar separado'}
                            </button>
                            <button
                              type="button"
                              disabled={acaoId === r.id}
                              onClick={() => abrirModalEntrega(r)}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                            >
                              <Clock className="w-3.5 h-3.5" />
                              {jaEntregaBase ? 'Atualizar entrega' : 'Confirmar entrega'}
                            </button>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            Encerrada
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {reposicoes.length === 0 && (
            <div className="text-center py-12 text-slate-500">Nenhuma requisição encontrada.</div>
          )}
        </div>
      )}

      {modalEntrega && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
            <button
              type="button"
              onClick={() => setModalEntrega(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold text-slate-900 pr-8">
              Confirmar entrega — {numeroSol(modalEntrega)}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Informe o dia e a hora em que o material foi entregue ao técnico{' '}
              <strong>{modalEntrega.tecnico_nome}</strong>.
            </p>
            <form onSubmit={confirmarEntregaBase} className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Dia</label>
                <input
                  type="date"
                  required
                  value={formEntrega.data}
                  onChange={(e) => setFormEntrega((f) => ({ ...f, data: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Hora</label>
                <input
                  type="time"
                  required
                  value={formEntrega.hora}
                  onChange={(e) => setFormEntrega((f) => ({ ...f, hora: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-3"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setModalEntrega(null)}
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvandoEntrega}
                  className="flex-1 px-4 py-3 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  {salvandoEntrega ? 'Salvando…' : 'Confirmar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReposicoesEstoque;
